import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useRecoilValue } from 'recoil';
import { useApiOperation } from 'src/api/hooks';
import { getPartnerSettingAsync, patchPartnerSettingAsync } from 'src/api/partner';
import { BaseButton, BaseTextarea, BaseTooltip, ContentsTitle } from 'src/components';
import BaseSectionTitle from 'src/components/layout/BaseSectionTitle';
import { usePartnerAuthority } from 'src/hooks/usePartnerAuthority';
import { useModal } from 'src/recoil/modalState/hook';
import { globalPartnerState } from 'src/recoil/partners/atom';

const activeTab = { label: "기본정보", value: "basicInfo" };

interface SettingFormData {
  welcomeText: string;
}

const SettingForm = () => {
  const { isAuthority, fetchAuthorityReadonly } = usePartnerAuthority();
  const { setBaseModal } = useModal();
  const partner = useRecoilValue(globalPartnerState);

  const { executeAsync: getPartnerSetting } = useApiOperation(getPartnerSettingAsync);
  const { executeAsync: patchPartnerSetting } = useApiOperation(patchPartnerSettingAsync);

  const [isStpm, setIsStpm] = React.useState(false);

  const { handleSubmit, setValue, control } = useForm<SettingFormData>({
    defaultValues: {
      welcomeText: "",
    },
  });

  // 파트너 설정 조회
  useEffect(() => {
    (async () => {
      if (!partner?.id) return;
      const { data } = await getPartnerSetting(String(partner.id));
      const setting = data?.data?.settings;
      const serviceAppCode = setting?.serviceApp?.code;
      if (setting?.welcomeText) {
        setValue("welcomeText", setting.welcomeText);
      }
      setIsStpm(serviceAppCode === "STPM");

    })();
  }, []);

  // 파트너 설정 저장
  const onSavePartnerSetting = async (formData: SettingFormData) => {
    if (!partner?.id) return;
    await patchPartnerSetting({
      partnerId: String(partner.id),
      welcomeText: formData.welcomeText,
    });
    setBaseModal({ isOpen: false });
  };

  const onSubmit = (data: SettingFormData) => {
    if (isAuthority("w")) {
      setBaseModal({
        isOpen: true,
        title: "저장하시겠습니까?",
        onClick: () => onSavePartnerSetting(data),
        onClose: () => setBaseModal({ isOpen: false }),
        btnLeftTitle: "취소",
        btnRightTitle: "저장",
      });
    } else {
      fetchAuthorityReadonly(true);
    }
  };

  const onError = (errors: any) => {
    console.log(errors);
  };

  return (
    <div>
      <ContentsTitle title="STPM 설정" tabs={[activeTab]} activeTab={activeTab} />

      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <div className="contents-container__scroll">
          <div className="contents-container__wrap">
            <article className="contents-container__wrap-article">
              <BaseSectionTitle title="제어 권한 설정" className="pb10" />

              <section className="contents-container__grid">
                <div className="contents-container__grid-index flex-start">
                  <p>홈화면 문구 설정</p>
                  <BaseTooltip contents="사용자 앱 홈화면에 표시될 문구를 지정할 수 있습니다.  이 기능은 SmartThings Pro Moblie 에서만 사용 가능합니다." touchIcon="QUESTION" />
                </div>
                <div className="contents-container__grid-contents">
                  <Controller
                    control={control}
                    name="welcomeText"
                    render={({ field: { onChange, value } }) => {
                      const handleChange = (newValue: string) => {
                        const lines = newValue.split("\n");
                        if (lines.length <= 2) {
                          onChange(newValue);
                        }
                      };
                      return (
                        <div className="flex-center-start flex-row minmax455">
                          <BaseTextarea
                            value={value}
                            onChange={handleChange}
                            placeholder={'미입력 시 "스마트한 공간 이용."으로 설정됩니다.'}
                            disabled={!isStpm}
                          />
                        </div>
                      );
                    }}
                  />
                  <ul className="base-list mt6">
                    <li>최대 2줄까지 입력 가능합니다. </li>
                    <li>한줄에 7~8글자 입력을 추천합니다.</li>
                    <li>문구가 길어질 경우 사용자 앱에서 말줄임표로 표시됩니다.</li>
                  </ul>
                </div>
              </section>
            </article>
          </div>
        </div>
        <div className="contents-container__btn-wrap">
          <div className="left-area"></div>
          <div className="right-area">{isAuthority("w") && <BaseButton type="submit" title="저장" className="size-large" />}</div>
        </div>
      </form>
    </div>
  );
};

export default SettingForm;
